import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Mail, AlertTriangle, CheckCircle2, RefreshCw, RotateCw } from "lucide-react";

const STATUS_STYLES = {
  sent:             "bg-emerald-100 text-emerald-700",
  failed:           "bg-red-100 text-red-700",
  skipped_no_key:   "bg-amber-100 text-amber-700",
};

export default function AdminEmails() {
  const [rows, setRows] = useState([]);
  const [configured, setConfigured] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/emails?limit=200");
      setRows(r.data.rows || []);
      setConfigured(r.data.resend_configured);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const resend = async (row) => {
    setBusy(row.email_id);
    try {
      await api.post(`/admin/emails/${row.email_id}/resend`);
      toast.success("Re-sent");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Resend failed"); }
    finally { setBusy(null); }
  };

  const skipped = rows.filter((r) => r.status === "skipped_no_key");

  return (
    <div data-testid="admin-emails" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Notifications</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">
            Email Log
          </h1>
        </div>
        <button onClick={load} className="btn-outline-navy text-xs inline-flex items-center gap-1">
          <RotateCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {!configured && (
        <div className="card-pb p-4 border-l-4 border-amber-500 flex items-start gap-3" data-testid="resend-not-configured">
          <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="text-sm">
            <div className="font-medium text-[#0A2540] dark:text-white">Resend API key not configured</div>
            <p className="text-xs text-[#475569] dark:text-slate-400 mt-1">
              Emails are being logged with status <code className="text-[10px] bg-slate-100 px-1 rounded">skipped_no_key</code>{" "}
              but not sent. Add <code className="text-[10px] bg-slate-100 px-1 rounded">RESEND_API_KEY</code> to{" "}
              <code className="text-[10px] bg-slate-100 px-1 rounded">/app/backend/.env</code> and restart the backend.
              {skipped.length > 0 && ` Once the key is live, use "Resend" below to send the ${skipped.length} skipped email${skipped.length === 1 ? "" : "s"}.`}
            </p>
          </div>
        </div>
      )}

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Kind</th>
              <th className="text-left px-4 py-2">To</th>
              <th className="text-left px-4 py-2">Subject</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">When</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-[#94A3B8] italic">No emails yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.email_id} data-testid={`email-row-${r.email_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700">
                <td className="px-4 py-2 inline-flex items-center gap-1.5">
                  <Mail className="w-3 h-3 text-[#C9A356]" /> {r.kind}
                </td>
                <td className="px-4 py-2 text-[#475569] dark:text-slate-400">{r.to}</td>
                <td className="px-4 py-2 text-[#0A2540] dark:text-white">{r.subject}</td>
                <td className="px-4 py-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${STATUS_STYLES[r.status] || "bg-slate-100 text-slate-600"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-[10px] text-[#94A3B8]">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-2 text-right">
                  {r.status !== "sent" && r.kind === "handoff_ready" && (
                    <button
                      onClick={() => resend(r)}
                      disabled={busy === r.email_id || (!configured && r.status !== "skipped_no_key")}
                      className="text-xs text-[#C9A356] hover:underline inline-flex items-center gap-0.5 disabled:opacity-50 disabled:no-underline"
                      data-testid={`resend-btn-${r.email_id}`}
                    >
                      <RefreshCw className="w-3 h-3" /> Resend
                    </button>
                  )}
                  {r.status === "sent" && (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 inline" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
